/**
 * ContactPage — public /contact route.
 *
 * Content per user directives:
 *  - Contact form (inline, NOT modal): category, subject, message, email.
 *  - Standard business hours (Mon–Fri, 9am–5pm MST). No physical address,
 *    no phone number, no visible email addresses.
 *  - Secondary "Book a Demo" CTA that opens the iter110 BookDemoModal.
 *  - Shares LandingNav + a minimal footer matching the landing page.
 *
 * The form POSTs anonymously to /api/support/tickets (same endpoint the
 * dashboard and landing ContactSupportModal already use).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Loader2, Clock, Calendar, LifeBuoy, MessageSquare } from 'lucide-react';
import LandingNav from '@/components/landing/LandingNav';
import BookDemoModal from '@/components/landing/BookDemoModal';
import DotboxLogo from '@/components/common/DotboxLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'feedback',        label: 'Feedback' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug',             label: 'Bug Report' },
  { value: 'billing',         label: 'Billing' },
  { value: 'other',           label: 'Other' },
];

const ContactPage = () => {
  const [showBookDemo, setShowBookDemo] = useState(false);
  const [category, setCategory] = useState('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory('feedback');
    setSubject('');
    setMessage('');
    setEmail('');
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();
    const trimmedEmail = email.trim();

    if (trimmedSubject.length < 3) { toast.error('Subject is too short (min 3 characters).'); return; }
    if (trimmedMessage.length < 10) { toast.error('Please tell us a bit more (min 10 characters).'); return; }
    if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      toast.error('Please enter a valid email so we can reply.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/support/tickets`, {
        category,
        subject: trimmedSubject,
        message: trimmedMessage,
        email: trimmedEmail,
        source_path: '/contact',
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      toast.success("Thanks — your message is on its way to our team.");
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A1024] text-slate-200 flex flex-col"
      data-testid="contact-page"
    >
      <LandingNav onOpenBookDemo={() => setShowBookDemo(true)} />

      {/* Hero */}
      <section className="relative border-b border-[#1F2A4D] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-transparent to-red-900/5 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">
            Contact
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
            Get in touch with the team.
          </h1>
          <p className="text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed">
            Send a message and we&apos;ll reply within one business day. Prefer a live conversation? Book a demo below.
          </p>
        </div>
      </section>

      {/* Main content */}
      <main className="flex-1 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column — hours + book a demo */}
            <div className="lg:col-span-1 space-y-4">
              <Card
                className="border"
                data-testid="contact-hours-card"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-red-400" />
                    <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                      Business Hours
                    </h3>
                  </div>
                  <div className="text-slate-200 text-base font-medium mb-1">
                    Mon – Fri · 9:00 AM – 5:00 PM
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">
                    Mountain Standard Time (MST)
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed mt-4">
                    Messages arriving outside business hours are answered on the next business day.
                  </p>
                </CardContent>
              </Card>

              <Card
                className="border border-red-600/40 shadow-lg shadow-red-900/10"
                data-testid="contact-book-demo-card"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4 text-red-400" />
                    <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                      Prefer a live walkthrough?
                    </h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed mb-4">
                    30 minutes with the dotbox.ai team. Confirmation, calendar invite, and video link arrive instantly.
                  </p>
                  <Button
                    onClick={() => setShowBookDemo(true)}
                    className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                    data-testid="contact-open-book-demo"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    Book a Demo
                  </Button>
                </CardContent>
              </Card>

              <Card
                className="border"
                data-testid="contact-reply-sla-card"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <LifeBuoy className="h-4 w-4 text-emerald-400" />
                    <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                      Reply SLA
                    </h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    Every ticket is triaged the same business day. Existing customers receive a reply within one business day; Enterprise plans include priority routing.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right column — contact form */}
            <div className="lg:col-span-2">
              <Card
                className="border"
                data-testid="contact-form-card"
              >
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="h-5 w-5 text-red-400" />
                    <h2 className="text-lg font-semibold text-white">
                      Send us a message
                    </h2>
                  </div>
                  <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="contact-category" className="text-slate-200 text-xs">Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                          <SelectTrigger
                            id="contact-category"
                            className="bg-[#0A1024] border-[#1F2A4D] text-slate-100"
                            data-testid="contact-category-trigger"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => (
                              <SelectItem
                                key={c.value}
                                value={c.value}
                                data-testid={`contact-category-${c.value}`}
                              >
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="contact-email" className="text-slate-200 text-xs">Your email *</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="contact-subject" className="text-slate-200 text-xs">Subject *</Label>
                      <Input
                        id="contact-subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Brief summary"
                        maxLength={200}
                        className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="contact-message" className="text-slate-200 text-xs">Message *</Label>
                      <Textarea
                        id="contact-message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us what's on your mind — include steps to reproduce if this is a bug."
                        rows={8}
                        maxLength={5000}
                        className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500 resize-none"
                        data-testid="contact-message"
                      />
                      <div className="text-[10px] text-slate-500 text-right">
                        {message.length}/5000
                      </div>
                    </div>

                    <div className="flex items-center justify-end pt-2">
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-6"
                        data-testid="contact-submit"
                      >
                        {submitting ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                        ) : (
                          'Send Message'
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Footer — matches landing page footer exactly (copyright verbatim) */}
      <footer className="bg-[#0A1024] border-t border-[#1F2A4D] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <DotboxLogo variant="dark" className="h-8" />
            <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4">
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}
                className="inline-flex items-center gap-1 text-slate-400 hover:text-rose-400 transition-colors text-sm"
                data-testid="contact-footer-home"
              >
                ← Back to home
              </Link>
              <span className="hidden md:inline text-slate-600">·</span>
              <p className="text-slate-500 text-sm">
                ©2026 dotbox.ai — All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <BookDemoModal open={showBookDemo} onOpenChange={setShowBookDemo} />
    </div>
  );
};

export default ContactPage;
